import { createBrowserRouter, Navigate } from 'react-router-dom';
import { PublicLayout } from '@/layouts/PublicLayout';
import { PortalLayout } from '@/layouts/PortalLayout';
import { AdminLayout } from '@/layouts/AdminLayout';
import { AuthGuard } from '@/components/auth/AuthGuard';
import { lazyNamed } from '@/lib/lazyNamed';

// ─── Eager (always needed: most-likely entry points + critical fallbacks) ──
import { HomePage } from '@/pages/public/HomePage';
import { LoginPage } from '@/pages/public/LoginPage';
import { RegisterPage } from '@/pages/public/RegisterPage';
import { NotFoundPage } from '@/pages/public/NotFoundPage';
import { ErrorPage } from '@/pages/public/ErrorPage';

// ─── Lazy public pages (own chunk each) ───────────────────────────────────
const AboutPage           = lazyNamed(() => import('@/pages/public/AboutPage'),           'AboutPage');
const ForDoctorsPage      = lazyNamed(() => import('@/pages/public/ForDoctorsPage'),      'ForDoctorsPage');
const ForClinicsPage      = lazyNamed(() => import('@/pages/public/ForClinicsPage'),      'ForClinicsPage');
const JobSearchPage       = lazyNamed(() => import('@/pages/public/JobSearchPage'),       'JobSearchPage');
const JobDetailPublicPage = lazyNamed(() => import('@/pages/public/JobDetailPublicPage'), 'JobDetailPublicPage');
const ContactPage         = lazyNamed(() => import('@/pages/public/ContactPage'),         'ContactPage');
const PrivacyPage         = lazyNamed(() => import('@/pages/public/PrivacyPage'),         'PrivacyPage');
const TermsPage           = lazyNamed(() => import('@/pages/public/TermsPage'),           'TermsPage');
const ForgotPasswordPage  = lazyNamed(() => import('@/pages/public/ForgotPasswordPage'),  'ForgotPasswordPage');
const ResetPasswordPage   = lazyNamed(() => import('@/pages/public/ResetPasswordPage'),   'ResetPasswordPage');
const RefereeCheckPage    = lazyNamed(() => import('@/pages/public/RefereeCheckPage'),    'RefereeCheckPage');
const SharedCredentialPackPage = lazyNamed(() => import('@/pages/public/SharedCredentialPackPage'), 'SharedCredentialPackPage');
const PublicDoctorProfilePage  = lazyNamed(() => import('@/pages/public/PublicDoctorProfilePage'),  'PublicDoctorProfilePage');

// ─── Lazy doctor portal pages ─────────────────────────────────────────────
const DoctorDashboardPage         = lazyNamed(() => import('@/pages/portal/DoctorDashboardPage'),         'DoctorDashboardPage');
const PortalJobsPage              = lazyNamed(() => import('@/pages/portal/PortalJobsPage'),              'PortalJobsPage');
const PortalJobDetailPage         = lazyNamed(() => import('@/pages/portal/PortalJobDetailPage'),         'PortalJobDetailPage');
const SavedJobsPage               = lazyNamed(() => import('@/pages/portal/SavedJobsPage'),               'SavedJobsPage');
const JobAlertsPage               = lazyNamed(() => import('@/pages/portal/JobAlertsPage'),               'JobAlertsPage');
const ApplicationsPage            = lazyNamed(() => import('@/pages/portal/ApplicationsPage'),            'ApplicationsPage');
const OffersPage                  = lazyNamed(() => import('@/pages/portal/OffersPage'),                  'OffersPage');
const ResumeManagerPage           = lazyNamed(() => import('@/pages/portal/ResumeManagerPage'),           'ResumeManagerPage');
const ResumePrintPage             = lazyNamed(() => import('@/pages/portal/ResumePrintPage'),             'ResumePrintPage');
const RefereesPage                = lazyNamed(() => import('@/pages/portal/RefereesPage'),                'RefereesPage');
const ComplianceLockerPage        = lazyNamed(() => import('@/pages/portal/ComplianceLockerPage'),        'ComplianceLockerPage');
const CredentialPackPage          = lazyNamed(() => import('@/pages/portal/CredentialPackPage'),          'CredentialPackPage');
const ChecklistDocumentsDoctorPage = lazyNamed(() => import('@/pages/portal/ChecklistDocumentsDoctorPage'),'ChecklistDocumentsDoctorPage');
const ProfileSettingsPage         = lazyNamed(() => import('@/pages/portal/ProfileSettingsPage'),         'ProfileSettingsPage');
const NotificationsPage           = lazyNamed(() => import('@/pages/portal/NotificationsPage'),           'NotificationsPage');
const HelpPage                    = lazyNamed(() => import('@/pages/portal/HelpPage'),                    'HelpPage');

// ─── Lazy admin pages ─────────────────────────────────────────────────────
const AdminDashboardPage         = lazyNamed(() => import('@/pages/admin/AdminDashboardPage'),         'AdminDashboardPage');
const CandidateAssessmentPage    = lazyNamed(() => import('@/pages/admin/CandidateAssessmentPage'),    'CandidateAssessmentPage');
const OfferManagementPage        = lazyNamed(() => import('@/pages/admin/OfferManagementPage'),        'OfferManagementPage');
const DoctorManagementPage       = lazyNamed(() => import('@/pages/admin/DoctorManagementPage'),       'DoctorManagementPage');
const AdminDoctorDetailPage      = lazyNamed(() => import('@/pages/admin/AdminDoctorDetailPage'),      'AdminDoctorDetailPage');
const JobManagementPage          = lazyNamed(() => import('@/pages/admin/JobManagementPage'),          'JobManagementPage');
const HospitalsPage              = lazyNamed(() => import('@/pages/admin/HospitalsPage'),              'HospitalsPage');
const HospitalDetailPage         = lazyNamed(() => import('@/pages/admin/HospitalDetailPage'),         'HospitalDetailPage');
const ComplianceVerificationPage = lazyNamed(() => import('@/pages/admin/ComplianceVerificationPage'), 'ComplianceVerificationPage');
const ChecklistDocumentsAdminPage = lazyNamed(() => import('@/pages/admin/ChecklistDocumentsAdminPage'),'ChecklistDocumentsAdminPage');
const AdminSettingsPage          = lazyNamed(() => import('@/pages/admin/AdminSettingsPage'),          'AdminSettingsPage');
const AuditTrailPage             = lazyNamed(() => import('@/pages/admin/AuditTrailPage'),             'AuditTrailPage');
const TeamManagementPage         = lazyNamed(() => import('@/pages/admin/TeamManagementPage'),         'TeamManagementPage');
const ErasureRequestsPage        = lazyNamed(() => import('@/pages/admin/ErasureRequestsPage'),        'ErasureRequestsPage');
const AdminRefereesPage          = lazyNamed(() => import('@/pages/admin/AdminRefereesPage'),          'AdminRefereesPage');

// Reusable error fallback for each top-level route. React Router calls this
// on loader/action errors AND on render errors thrown by route children.
const errorElement = <ErrorPage />;

export const router = createBrowserRouter([
  // ─── Public ─────────────────────────────────────────────────────────
  {
    element: <PublicLayout />,
    errorElement,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/about', element: <AboutPage /> },
      { path: '/for-doctors', element: <ForDoctorsPage /> },
      { path: '/for-clinics', element: <ForClinicsPage /> },
      { path: '/jobs', element: <JobSearchPage /> },
      { path: '/jobs/:id', element: <JobDetailPublicPage /> },
      { path: '/contact', element: <ContactPage /> },
      { path: '/privacy', element: <PrivacyPage /> },
      { path: '/terms', element: <TermsPage /> },
      { path: '/login', element: <LoginPage /> },
      // common alternate paths users type by habit
      { path: '/sign-in', element: <Navigate to="/login" replace /> },
      { path: '/signin', element: <Navigate to="/login" replace /> },
      { path: '/sign-up', element: <Navigate to="/register" replace /> },
      { path: '/signup', element: <Navigate to="/register" replace /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
    ],
  },

  // ─── Doctor portal ──────────────────────────────────────────────────
  {
    path: '/portal',
    element: (
      <AuthGuard requiredRole="doctor">
        <PortalLayout />
      </AuthGuard>
    ),
    errorElement,
    children: [
      { index: true, element: <Navigate to="/portal/dashboard" replace /> },
      { path: 'dashboard', element: <DoctorDashboardPage /> },
      { path: 'jobs', element: <PortalJobsPage /> },
      { path: 'jobs/:id', element: <PortalJobDetailPage /> },
      { path: 'saved', element: <SavedJobsPage /> },
      { path: 'job-alerts', element: <JobAlertsPage /> },
      { path: 'applications', element: <ApplicationsPage /> },
      { path: 'offers', element: <OffersPage /> },
      { path: 'resumes', element: <ResumeManagerPage /> },
      { path: 'referees', element: <RefereesPage /> },
      { path: 'compliance', element: <ComplianceLockerPage /> },
      { path: 'credential-pack', element: <CredentialPackPage /> },
      { path: 'checklist-documents', element: <ChecklistDocumentsDoctorPage /> },
      { path: 'profile', element: <ProfileSettingsPage /> },
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'help', element: <HelpPage /> },
    ],
  },

  // ─── Admin ──────────────────────────────────────────────────────────
  {
    path: '/admin',
    element: (
      <AuthGuard requiredRole="admin">
        <AdminLayout />
      </AuthGuard>
    ),
    errorElement,
    children: [
      { index: true, element: <Navigate to="/admin/dashboard" replace /> },
      { path: 'dashboard', element: <AdminDashboardPage /> },
      { path: 'applications', element: <CandidateAssessmentPage /> },
      { path: 'offers', element: <OfferManagementPage /> },
      { path: 'doctors', element: <DoctorManagementPage /> },
      { path: 'doctors/:id', element: <AdminDoctorDetailPage /> },
      { path: 'jobs', element: <JobManagementPage /> },
      { path: 'hospitals', element: <HospitalsPage /> },
      { path: 'hospitals/:id', element: <HospitalDetailPage /> },
      { path: 'compliance', element: <ComplianceVerificationPage /> },
      { path: 'checklist-documents', element: <ChecklistDocumentsAdminPage /> },
      { path: 'settings', element: <AdminSettingsPage /> },
      { path: 'audit', element: <AuditTrailPage /> },
      { path: 'team', element: <TeamManagementPage /> },
      { path: 'erasure-requests', element: <ErasureRequestsPage /> },
      { path: 'referees', element: <AdminRefereesPage /> },
    ],
  },

  // ─── Standalone print/preview pages — auth-guarded but NO chrome ────
  // Rendered outside PortalLayout so the print preview shows just the
  // resume body (no sidebar, no header).
  {
    path: '/portal/resumes/:id/print',
    element: (
      <AuthGuard requiredRole="doctor">
        <ResumePrintPage />
      </AuthGuard>
    ),
    errorElement: <ErrorPage />,
  },

  // ─── Public token-based pages (no chrome, no auth) ──────────────────
  {
    path: '/referee-check/:token',
    element: <RefereeCheckPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/share/credentials/:token',
    element: <SharedCredentialPackPage />,
    errorElement: <ErrorPage />,
  },
  // SEO-friendly public doctor profile (opt-in by the doctor)
  {
    path: '/d/:slug',
    element: <PublicDoctorProfilePage />,
    errorElement: <ErrorPage />,
  },

  { path: '*', element: <NotFoundPage /> },
]);
